import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { pdfViewerStyles } from "@entrilia/shared/components/previewFile/pdfViewer/pdfViewerStyles";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { combineComparers, priorityComparer, stringComparerBy } from "@entrilia/shared/utilities/arrayHelper";
import { autoFormatCamelCase } from "@entrilia/shared/utilities/stringHelper";
import adminApi from "../../../../api/adminApi";
import type { KnownCategoryTypes } from "../../../../api/types/accessTypes";
import useClientContext from "../../../../context/useClientContext";
import { categoryTypeCaptionsMap } from "../../../../utilities/enumCaptions";
import MultiselectAutocomplete from "../../../common/MultiselectAutocomplete";
import WatermarkDateWarning from "./WatermarkDateWarning";
import WatermarkPreview from "./WatermarkPreview";
import { watermarkSegmentCaptionMap } from "./watermarkSettingsHelper";
import type { WatermarkCategoriesOption, WatermarkItemKey, WatermarkSettingsState } from "./watermarkSettingsState";
import {
  getWatermarkSettingsStateFromSavedSettings,
  mapWatermarkSettingsStateToUpdateRequest,
  setCategoriesOptionAction,
  setCustomTextAction,
  setExternalCategoryIdsAction,
  setIncludeTimeAction,
  setItemVisibilityAction,
  setWatermarkEnabledAction,
  validateWatermarkSettingsState,
} from "./watermarkSettingsState";

const updateWatermarkSettings = withErrorHandling(adminApi.updateWatermarkSettings);

interface CategoryOption {
  externalId: string;
  name: string;
  group: string;
  categoryType: KnownCategoryTypes;
}

const WatermarkSettingsSection = () => {
  const { hasPermissions, accessCategories } = useClientContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [settingsState, setSettingsState] = useState<WatermarkSettingsState>();
  const [loading, setLoading] = useState(false);

  const [savedSettings, settingsError, { setData: setSavedSettings }] = useFetch(adminApi.getWatermarkSettings, {
    onSetData: (data) => {
      setSettingsState(getWatermarkSettingsStateFromSavedSettings(data));
    },
  });

  const categoryOptions = useMemo<CategoryOption[]>(() => {
    const sortComparer = combineComparers<CategoryOption>(
      priorityComparer((c) => c.categoryType === "Message"),
      stringComparerBy((c) => c.name)
    );

    return accessCategories
      .filter((c) => ["Message", "Fundraising"].includes(c.type))
      .map((c) => ({
        externalId: c.externalId,
        name: c.name,
        group: categoryTypeCaptionsMap.get(c.type) ?? autoFormatCamelCase(c.type),
        categoryType: c.type,
      }))
      .sort(sortComparer);
  }, [accessCategories]);

  if (settingsError) {
    logError(settingsError, "WatermarkSettingsPage");
    return <DataLoadingFailed title="Could not load watermark settings" sx={{ height: "100%" }} />;
  }

  if (!settingsState || !savedSettings) {
    return <InlineLoader />;
  }

  const hasPermissionsToEdit = hasPermissions(["ManageInvestorPortalSettings"]);
  const isEditingDisabled = !hasPermissionsToEdit || !settingsState.watermarkEnabled;

  const handleWatermarkEnabledToggle = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setSettingsState((prev) => (prev ? setWatermarkEnabledAction(checked)(prev) : prev));
  };

  const handleCategoriesOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSettingsState((prev) =>
      prev ? setCategoriesOptionAction(event.target.value as WatermarkCategoriesOption)(prev) : prev
    );
  };

  const handleSelectedCategoriesChange = (newCategories: CategoryOption[]) => {
    setSettingsState((prev) =>
      prev ? setExternalCategoryIdsAction(newCategories.map((category) => category.externalId))(prev) : prev
    );
  };

  const handleWatermarkItemToggle =
    (itemKey: WatermarkItemKey) => (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setSettingsState((prev) => (prev ? setItemVisibilityAction(itemKey, checked)(prev) : prev));
    };

  const handleIncludeTimeToggle = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setSettingsState((prev) => (prev ? setIncludeTimeAction(checked)(prev) : prev));
  };

  const handleCustomTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSettingsState((prev) => (prev ? setCustomTextAction(event.target.value)(prev) : prev));
  };

  const handleSave = async () => {
    setLoading(true);

    const data = mapWatermarkSettingsStateToUpdateRequest(settingsState);

    const [updatedSettings, error] = await updateWatermarkSettings(data);

    setLoading(false);

    if (error) {
      sendNotificationError(error.message);
      logError(error, "[WatermarkSettingsPage] updateWatermarkSettings");
      return;
    }

    sendNotification("Watermark settings saved successfully");
    setSettingsState(getWatermarkSettingsStateFromSavedSettings(updatedSettings));
    setSavedSettings(updatedSettings);
  };

  const handleReset = () => {
    setSettingsState(getWatermarkSettingsStateFromSavedSettings(savedSettings));
  };

  const validationResult = validateWatermarkSettingsState(settingsState);

  const selectedCategories = categoryOptions.filter((category) =>
    settingsState.externalCategoryIds.includes(category.externalId)
  );

  return (
    <Grid container sx={{ height: "100%" }}>
      <Grid size={3} sx={{ py: 2.5, px: 3, gap: 3, display: "flex", flexDirection: "column" }}>
        <Stack spacing={2}>
          <Stack spacing={1}>
            <Typography variant="subtitle1">Watermark Settings</Typography>
            <Typography color="secondary">
              The watermark will be applied both when viewing and downloading PDF files.
            </Typography>
          </Stack>
          <FormControlLabel
            disabled={!hasPermissionsToEdit}
            control={<Switch checked={settingsState.watermarkEnabled} onChange={handleWatermarkEnabledToggle} />}
            label="Watermark PDF files"
          />
        </Stack>

        <Divider />

        <Stack spacing={0.5}>
          <Typography variant="subtitle1">Document Categories</Typography>
          <RadioGroup
            defaultValue="all_categories"
            value={settingsState.categoriesOption}
            onChange={handleCategoriesOptionChange}
          >
            <FormControlLabel
              control={<Radio />}
              disabled={isEditingDisabled}
              value="all_categories"
              label="Apply to all categories"
            />
            <FormControlLabel
              control={<Radio />}
              disabled={isEditingDisabled}
              value="selected_categories"
              label="Apply to selected categories only"
            />
          </RadioGroup>

          {settingsState.categoriesOption === "selected_categories" && (
            <MultiselectAutocomplete
              options={categoryOptions}
              values={selectedCategories}
              disabled={isEditingDisabled}
              onSelected={handleSelectedCategoriesChange}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.externalId}
              groupBy={(option) => option.group}
              placeholder="Add categories"
              error={Boolean(validationResult.documentCategoriesError)}
              helperText={validationResult.documentCategoriesError}
              dense
            />
          )}
        </Stack>

        <Divider />

        <Stack spacing={0.5}>
          <Typography variant="subtitle1">Details</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={settingsState.items.customText.isVisible}
                onChange={handleWatermarkItemToggle("customText")}
              />
            }
            disabled={isEditingDisabled}
            label={watermarkSegmentCaptionMap[settingsState.items.customText.id]}
          />
          {settingsState.items.customText.isVisible && (
            <Box sx={{ pl: 1 }}>
              <TextField
                value={settingsState.items.customText.value}
                onChange={handleCustomTextChange}
                disabled={isEditingDisabled}
                fullWidth
                slotProps={{
                  htmlInput: { maxLength: 50 },
                  input: {
                    endAdornment: (
                      <Typography variant="caption" color="textSecondary">
                        {50 - settingsState.items.customText.value.length}
                      </Typography>
                    ),
                  },
                }}
                error={Boolean(validationResult.customTextError)}
                helperText={validationResult.customTextError}
              />
            </Box>
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={settingsState.items.contactName.isVisible}
                onChange={handleWatermarkItemToggle("contactName")}
              />
            }
            disabled={isEditingDisabled}
            label={watermarkSegmentCaptionMap[settingsState.items.contactName.id]}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={settingsState.items.contactEmail.isVisible}
                onChange={handleWatermarkItemToggle("contactEmail")}
              />
            }
            disabled={isEditingDisabled}
            label={watermarkSegmentCaptionMap[settingsState.items.contactEmail.id]}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={settingsState.items.investorName.isVisible}
                onChange={handleWatermarkItemToggle("investorName")}
              />
            }
            disabled={isEditingDisabled}
            label={watermarkSegmentCaptionMap[settingsState.items.investorName.id]}
          />
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={settingsState.items.dateAndTime.isVisible}
                  onChange={handleWatermarkItemToggle("dateAndTime")}
                />
              }
              disabled={isEditingDisabled}
              label={watermarkSegmentCaptionMap[settingsState.items.dateAndTime.id]}
            />
            {settingsState.items.dateAndTime.isVisible && (
              <FormControlLabel
                control={<Switch checked={settingsState.includeTime} onChange={handleIncludeTimeToggle} />}
                disabled={isEditingDisabled}
                label="Include Time"
                sx={{ ml: 0, mr: 0 }}
              />
            )}
          </Stack>
          {settingsState.items.dateAndTime.isVisible && (
            <Box sx={{ pl: 1 }}>
              <WatermarkDateWarning includeTime={settingsState.includeTime} />
            </Box>
          )}
          <FormHelperText error={Boolean(validationResult.enabledItemsError)}>
            {validationResult.enabledItemsError ?? ""}
          </FormHelperText>
        </Stack>

        {hasPermissionsToEdit && <Divider />}

        {hasPermissionsToEdit && (
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              disabled={!settingsState.isDirty || !validationResult.isValid}
              loading={loading}
              onClick={handleSave}
            >
              Save
            </Button>
            <Button variant="text" color="secondary" disabled={!settingsState.isDirty} onClick={handleReset}>
              Cancel
            </Button>
          </Stack>
        )}
      </Grid>
      <Grid
        size={9}
        sx={[
          { display: "flex", flexDirection: "column" },
          (t) => ({ borderLeft: "1px solid", borderColor: t.palette.divider }),
        ]}
      >
        <Stack>
          <Box sx={[{ py: 2, px: 2.5 }, (t) => ({ borderBottom: "1px solid", borderColor: t.palette.divider })]}>
            <Typography variant="subtitle1">Watermark Preview</Typography>
          </Box>
        </Stack>
        <Stack sx={{ height: "100%", bgcolor: pdfViewerStyles.bgcolor, p: 2 }}>
          <WatermarkPreview
            watermarkEnabled={settingsState.watermarkEnabled}
            items={Object.values(settingsState.items)}
          />
        </Stack>
      </Grid>
    </Grid>
  );
};

export default WatermarkSettingsSection;
